import { forwardRef, useImperativeHandle, useRef } from 'react';
// eslint-disable-next-line import/no-unresolved
import Loader from '../../../assets/loaders/circle-loader.svg?react';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import Picto from '../../atoms/Picto/Picto';

// Style
import style from './NavigationSearch.module.css';

/**
 * NavigationSearch component - A navigation search with icon, label and states
 * @param {object} params
 * @param {string} params.id - Input id
 * @param {string} params.placeholder - Input placeholder
 * @param {boolean} [params.isMenuOpen=false] - Whether the menu is open
 * @param {string} [params.className] - Additional CSS classes
 * @param {() => void} params.onFocus - Focus handler
 * @param {React.ReactNode} [params.children] - Children elements
 * @param {'menu'|'form'} [params.theme='menu'] - Theme of the search
 * @param {boolean} [params.disabled=false] - Whether the input is disabled
 * @param {React.ChangeEventHandler<HTMLInputElement>} [params.onChange] - Change handler
 * @param {React.KeyboardEventHandler<HTMLInputElement>} [params.onKeyDown] - KeyDown handler
 * @param {string} [params.value] - Input value
 * @param {string} [params.defaultValue] - Input defaultValue
 * @param {() => void} [params.onClear] - Clear handler
 * @param {boolean} [params.isLoading=false] - Whether the input is loading
 * @param {boolean} [params.hasIcon=true] - Whether the input has an icon
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element
 * @returns {JSX.Element} NavigationSearch component
 */
function NavigationSearchComponent({
  id = 'navigation-search',
  placeholder,
  isMenuOpen = false,
  onFocus,
  className,
  children,
  theme = 'menu',
  disabled = false,
  onChange,
  onKeyDown,
  value,
  defaultValue,
  onClear,
  isLoading = false,
  hasIcon = true,
}, ref) {
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => inputRef.current);

  /**
   * @param {React.MouseEvent<HTMLButtonElement>} e
   */
  const handleClearInput = (e) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current.value = '';
    onClear();
  };

  return (
    <label
      onFocus={onFocus}
      htmlFor={id}
      className={cn([
        style['navigation-search'],
        isMenuOpen && style['menu-open'],
        theme === 'form' && style['form-theme'],
        disabled && style.disabled,
        className,
      ])}
    >
      {hasIcon ? <Picto icon="search-normal" className={style.icon} /> : null}
      <input
        autoComplete="off"
        id={id}
        type="text"
        placeholder={placeholder}
        className={cn([style.input])}
        ref={inputRef}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={value}
        defaultValue={defaultValue}
      />
      {value ? (
        <button type="button" className={style.close} onClick={handleClearInput}>
          {isLoading ? <Loader className={style.loader} /> : <Picto icon="close-md" />}
        </button>
      ) : null}

      {children ? (
        <div className={style['actions-container']}>
          {children}
        </div>
      ) : null}
    </label>
  );
}

const NavigationSearch = forwardRef(NavigationSearchComponent);

export default NavigationSearch;
